import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { useGetPromotionsQuery } from 'store/api/strapiService/promotionsController';
import { viewModeSelector } from 'store/slices/viewMode';

import { findPathPages } from 'utils/pages';
import { getDateFilters, getSliderData } from 'utils/promotions';
import { VIEW_MODE } from 'utils/viewMode';

import ArrowCircleLink from 'components/UI/ArrowCircleLink/ArrowCircleLink';
import Loader from 'components/UI/Loader/Loader';
import Slider from 'components/UI/Slider/Slider';
import Card from 'components/pages/Promotions/Card/Card';

import { PAGE_PATH } from 'components/config/pages';
import { CATEGORIES, LIMITS } from 'components/config/promotions';

import { ReactComponent as PromoIcon } from 'images/Icons/Promo.svg';

import styles from 'components/pages/Promotions/Promotions.module.scss';

const itemsSizes = {
  running: {
    [VIEW_MODE.SIMPLE]: 3,
    [VIEW_MODE.WIDE]: 4,
  },
  comingSoon: {
    [VIEW_MODE.SIMPLE]: 3,
    [VIEW_MODE.WIDE]: 4,
  },
  ended: {
    [VIEW_MODE.SIMPLE]: 4,
    [VIEW_MODE.WIDE]: 6,
  },
};

const date = new Date();

const Promotions = () => {
  const { provider } = useParams();

  const viewMode = useSelector(viewModeSelector.getViewMode);

  const { data: runningData, isFetching: runningFetching } =
    useGetPromotionsQuery({
      provider,
      dateFilters: getDateFilters(CATEGORIES.running.value, date),
      limit: LIMITS.running,
      sortByMain: true,
    });

  const { data: comingSoonData, isFetching: comingSoonFetching } =
    useGetPromotionsQuery({
      provider,
      dateFilters: getDateFilters(CATEGORIES.comingSoon.value, date),
      limit: LIMITS.comingSoon,
    });

  const { data: endedData, isLoading: endedFetching } = useGetPromotionsQuery({
    provider,
    dateFilters: getDateFilters(CATEGORIES.ended.value, date),
    limit: LIMITS.ended,
  });

  const { data: running, meta: runningMeta } = runningData ?? {};
  const { data: comingSoon, meta: comingSoonMeta } = comingSoonData ?? {};
  const { data: ended, meta: endedMeta } = endedData ?? {};

  const items = useMemo(() => {
    const getVisible = (items, size) => items?.slice(0, size);
    const getTotal = (meta) => meta?.pagination?.total;

    return {
      running: {
        visible: getVisible(running, itemsSizes.running[viewMode]),
        total: getTotal(runningMeta),
      },
      comingSoon: {
        visible: getVisible(comingSoon, itemsSizes.comingSoon[viewMode]),
        total: getTotal(comingSoonMeta),
      },
      ended: {
        visible: getVisible(ended, itemsSizes.ended[viewMode]),
        total: getTotal(endedMeta),
      },
    };
  }, [runningData, comingSoonData, endedData, viewMode]);

  const sliderData = useMemo(
    () => getSliderData(items.running.visible),
    [items],
  );

  if ([runningFetching, comingSoonFetching, endedFetching].some(Boolean))
    return <Loader />;

  return (
    <div className={styles.wrapper}>
      {!!items.running.visible?.length && (
        <div className={styles.running}>
          <div className={styles.title}>
            <div>
              <span>Running</span>
              <PromoIcon />
              <span>{items.running.total}</span>
            </div>
            <div>
              {items.running.total > items.running.visible.length && (
                <ArrowCircleLink
                  path={findPathPages(PAGE_PATH.PROMOTIONS_CATEGORY)
                    .replace(':provider', provider)
                    .replace(':category', CATEGORIES.running.value)}
                  title="All Running"
                />
              )}
            </div>
          </div>
          <div className={styles.content}>
            <Slider type="horizontal" data={sliderData} />
          </div>
        </div>
      )}
      {!!items.comingSoon.visible?.length && (
        <div className={styles.comingSoon}>
          <div className={styles.title}>
            <div>
              <span>Coming soon</span>
              <PromoIcon />
              <span>{items.comingSoon.total}</span>
            </div>
            <div>
              {items.comingSoon.total > items.comingSoon.visible.length && (
                <ArrowCircleLink
                  path={findPathPages(PAGE_PATH.PROMOTIONS_CATEGORY)
                    .replace(':provider', provider)
                    .replace(':category', CATEGORIES.comingSoon.value)}
                  title="All Coming Soon"
                />
              )}
            </div>
          </div>
          <div className={styles.content}>
            {items.comingSoon.visible.map((item) => (
              <Card
                key={item.documentId}
                item={item}
                category="comingSoon"
                provider={provider}
              />
            ))}
          </div>
        </div>
      )}
      {!!items.ended.visible?.length && (
        <div className={styles.ended}>
          <div className={styles.title}>
            <div>
              <span>Ended</span>
              <PromoIcon />
              <span>{items.ended.total}</span>
            </div>
            <div>
              {items.ended.total > items.ended.visible.length && (
                <ArrowCircleLink
                  path={findPathPages(PAGE_PATH.PROMOTIONS_CATEGORY)
                    .replace(':provider', provider)
                    .replace(':category', CATEGORIES.ended.value)}
                  title="All Ended"
                />
              )}
            </div>
          </div>
          <div className={styles.content}>
            {items.ended.visible.map((item) => (
              <Card
                key={item.documentId}
                item={item}
                category="ended"
                provider={provider}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Promotions;
